/**
 * Réglages (SPEC-UI §7.3, §7.6 — tâche M2.29) — panneau posé sur la chambre.
 *
 * Deux réglages seulement, et ils comptent tous les deux :
 *
 * - **Tolérance de timing** : fenêtres et chronos ×1,30, score plafonné à 85. Ce n'est pas un
 *   mode facile honteux, c'est de l'accessibilité — et le plafond est là pour que ça reste un
 *   choix, pas un exploit. Le réglage est modifiable **entre deux étapes**, jamais pendant.
 * - **Calage audio** : −200 à +200 ms par pas de 5, et le mini-test de §7.7 (8 temps à 90 BPM,
 *   Espace, médiane proposée — `CalageAudio.tsx`). La valeur est lue par le Montage depuis
 *   ⟦A-76⟧ : chaque frappe est corrigée à la capture, que la musique joue ou non.
 *
 * ⚠️ Écart **résorbé côté compte** : SPEC-UI §7.3 fait appliquer la tolérance par le serveur à
 * la génération du pattern (`toleranceApplied`) et le cap 85 à la soumission. C'est désormais
 * ce qui se passe — le réglage part en `PATCH /api/runs/:id/settings` (§5.9) et entre dans
 * `flags.accessibility_tolerance`. En bac à sable il n'y a pas de serveur : le réglage vit dans
 * `uiStore` et c'est le générateur de pattern client qui le lit.
 *
 * L'écran porte aussi **le choix de l'endroit où la partie est calculée** — la seule décision
 * de cet écran qui change le sens des autres.
 *
 * ## ⟦C57 · lot 5c · A-174⟧ Moins de texte : le libellé et le réglage à l'écran, l'aide derrière « Les dessous »
 *
 * Matt, 02/10/2026 : *« Il y a trop de trucs écrits partout, on ne lit plus rien à la fin. »* L'écran
 * des Réglages était le 4ᵉ du classement (244 mots visibles, 527 avec le défilement). Chaque réglage
 * garde son libellé court et son interrupteur (ou son curseur), les chiffres qui comptent (« +30 % »,
 * « 85 », « +20 ms ») et, au plus, une phrase de 12 mots. **Les aides, elles, n'ont pas bougé d'une
 * lettre** : elles passent derrière un pictogramme « Les dessous » par groupe de réglages (Pendant le
 * jeu, Quand tu publies, Son et calage, Ta partie, Abandonner), le composant partagé de tout le jeu
 * (`components/LesDessous.tsx`). Les confirmations (changer d'endroit, se déconnecter, abandonner)
 * tiennent en une phrase ; leur version longue vit dans les dessous du groupe qui les déclenche.
 * Le Menu ouvre ces mêmes Réglages (`Menu.tsx`, `onClose`) : une seule modification pour les deux portes.
 */

import { useNavigate } from "react-router-dom";

import {
  BEATS_TEST,
  BPM_TEST,
  OFFSET_MAX_MS,
  OFFSET_MIN_MS,
  OFFSET_PAS_MS,
} from "../../audio/calage";
import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { Panneau } from "../../components/Panneau";
import {
  modeActif,
  synchroniserManagerDelai,
  synchroniserRevelation,
  synchroniserTolerance,
} from "../../game/client";
import { systemeOuvert } from "../../game/menage";
import { useDecouverteStore } from "../../stores/decouverteStore";
import { useSessionStore } from "../../stores/sessionStore";
import { dialog, useUIStore } from "../../stores/uiStore";
import { useRunStore } from "../../stores/runStore";

import { CalageAudio } from "./CalageAudio";

export function Reglages({ onClose }: { onClose?: () => void } = {}) {
  const settings = useUIStore((s) => s.settings);
  const setSetting = useUIStore((s) => s.setSetting);
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  const run = useRunStore((s) => s.run);
  const abandonner = useRunStore((s) => s.abandonner);
  const leconsVues = useDecouverteStore((s) => s.vues.length);
  const reinitialiserDecouverte = useDecouverteStore((s) => s.reinitialiser);
  const utilisateur = useSessionStore((s) => s.utilisateur);
  const deconnecter = useSessionStore((s) => s.deconnecter);
  const revenirAuMenu = useSessionStore((s) => s.revenirAuMenu);
  const voirMenu = useSessionStore((s) => s.voirMenu);
  const compte = modeActif() === "compte";
  // ⟦C60 bis · A-177⟧ La réputation a quitté l'écran (arbitrage n° 2, 03/10/2026) : la liste des jauges du
  // tableau de bord ne la nomme plus. Rallumée, c'est la phrase d'avant, mot pour mot.
  const jaugesDuHud = systemeOuvert("reputation")
    ? "réputation, fidélité, momentum, hype, argent, niveau"
    : "fidélité, momentum, hype, argent, niveau";
  const naviguer = useNavigate();

  /**
   * Changer d'endroit où la partie est calculée n'est pas anodin : les deux mondes sont
   * étanches (le miroir local et la base ne se parlent pas), et une run de bac à sable ne
   * monte pas dans un compte. On le dit **avant**, avec un dialogue maison — jamais
   * `window.confirm` (CLAUDE.md).
   */
  async function changerDeMonde() {
    // ⟦C57 · lot 5c⟧ Une phrase. La version longue (« au tour exact où tu l'as laissée ») vit dans
    // les dessous du groupe « Ta partie » (bloc « Changer d'endroit », plus bas).
    const ok = await dialog.confirm({
      title: "Changer d'endroit de calcul ?",
      body: compte
        ? "Retour au menu : ta chaîne reste intacte sur le serveur."
        : "Retour au menu : ta partie reste intacte dans ce navigateur.",
      confirmLabel: "Repose-moi la question",
      cancelLabel: "Rester ici",
    });
    if (!ok) return;
    revenirAuMenu();
  }

  /**
   * ⟦C17⟧ « Menu principal » — un aller simple, PAS un abandon : contrairement à
   * `revenirAuMenu()` (qui oublie le mode et vide le miroir, ⟦A-141⟧ « repose la question »)
   * et à `abandonnerLaChaine()` plus bas (qui clôt la chaîne pour de bon), ce bouton laisse la
   * run exactement telle quelle en mémoire — c'est ce qui permet à l'écran-titre de proposer
   * « Quitter » (revenir ici) ET « Charger » (y retourner instantanément, sans recharger quoi
   * que ce soit) pour la MÊME partie.
   */
  function allerAuMenu() {
    voirMenu();
  }

  async function seDeconnecter() {
    // ⟦C57 · lot 5c⟧ Une phrase ; la longue (« Il suffira de te reconnecter pour la reprendre ») est dans les dessous.
    const ok = await dialog.confirm({
      title: "Se déconnecter ?",
      body: "Ta chaîne reste au tour exact : reconnecte-toi pour la reprendre.",
      confirmLabel: "Me déconnecter",
      cancelLabel: "Rester connecté",
    });
    if (!ok) return;
    await deconnecter();
  }

  /**
   * ⟦A-97 §1.2⟧ **Abandonner la chaîne en cours** — le manque n° 1 de l'avenant.
   *
   * `POST /api/runs/:id/abandon` existait depuis M2, mais **aucun écran ne l'appelait sur une
   * run vivante** : le seul appelant était le bouton « Repartir de zéro » de l'écran de fin.
   * Autrement dit, le joueur qui voulait tourner la page devait d'abord mourir ou gagner — ou
   * sortir un `curl`. C'est le bouton qui manquait, et il vit ici parce que les Réglages sont
   * le panneau qu'on ouvre quand on veut agir sur la partie elle-même, pas sur son contenu.
   *
   * **Le texte dit la vérité, et elle n'est pas la même des deux côtés** (même règle qu'ailleurs
   * dans cet écran) : côté compte la chaîne rejoint le mémorial et se relit — PLAN §4.11, « une
   * run n'est JAMAIS supprimée » ; en bac à sable elle est réellement effacée du navigateur.
   * Promettre le mémorial au bac à sable, ou l'effacement au compte, serait mentir dans les deux
   * sens.
   *
   * ⚠️ **On navigue sur la réponse de la route, jamais sur `state.status`.** L'abandon n'écrit
   * pas `run_state` : après le 200, le JSON d'état porte toujours `status: "active"` — c'est
   * `runs.status` qui passe à `ended`. Un routage qui attendrait `game_over` dans l'état ne
   * partirait donc jamais. `abandonner()` (runStore) attend l'appel, puis remet `run` à `null` :
   * c'est cette remise à zéro, obtenue APRÈS la réponse du serveur, qui fait basculer le routeur.
   *
   * Un échec réseau **laisse la run en place** et le dit : `ServerGameClient.abandonner()` ne
   * ravale que le `409 run_not_active` (déjà terminée ⇒ l'abandon a bien eu lieu) et relance
   * tout le reste. Avaler l'erreur ici afficherait « c'est fait » sur une chaîne toujours vivante.
   */
  async function abandonnerLaChaine() {
    if (!run) return;
    // ⟦C57 · lot 5c⟧ Une phrase, et elle dit toujours la vérité des deux côtés (mémorial / effacée du
    // navigateur). Le texte long de l'ancienne confirmation (épitaphe, « on ne revient pas dessus »)
    // est dans les dessous du groupe « Abandonner cette chaîne », juste au-dessus du bouton.
    const ok = await dialog.confirm({
      title: "Abandonner cette chaîne ?",
      body: compte
        ? `« ${run.channelName} » rejoint le mémorial : relisible, jamais rejouable.`
        : `« ${run.channelName} » est effacée de ce navigateur : seule sa fiche reste.`,
      confirmLabel: "Abandonner pour de bon",
      cancelLabel: "Continuer à jouer",
      danger: true,
    });
    if (!ok) return;

    try {
      await abandonner();
    } catch {
      await dialog.info({
        title: "La chaîne n'a pas été abandonnée",
        body: "Le serveur n'a pas confirmé : ta chaîne est intacte, réessaie.",
        confirmLabel: "Compris",
        icone: "⚠️",
      });
      return;
    }
    naviguer("/nouvelle-run", { replace: true });
  }

  // ⟦C57 · lot 5c⟧ Le compteur de leçons lues, en trois mots : « 3 explications lues ».
  const lues = `${leconsVues} explication${leconsVues > 1 ? "s" : ""} lue${leconsVues > 1 ? "s" : ""}`;

  return (
    <Panneau titre="⚙ Réglages" onClose={onClose}>
      {/* ⟦C57 · lot 5c⟧ Groupe 1 : ce qui change la façon de jouer. Chaque réglage ne montre que son
          libellé et son interrupteur ; l'aide de chacun est dans « Les dessous » du groupe. */}
      <fieldset className="reglage-bloc">
        <legend>
          <span className="reglage-legende">
            <strong>Pendant le jeu</strong>
            <LesDessous titre="Pendant le jeu" variante="icone" className="reglage-dessous">
              <DessousBloc titre="Mode découverte">
                <p className="dessous-texte">
                  M'expliquer chaque mécanique la première fois qu'elle arrive.
                </p>
                <p className="dessous-texte">
                  Le jeu se fige, l'écran s'assombrit, et une fiche t'explique ce qui vient de
                  s'ouvrir — les jauges, les mini-jeux, les événements. Une fois chacune, par
                  partie.
                </p>
                <p className="dessous-texte">
                  {leconsVues === 0
                    ? "Rien de lu pour l'instant dans cette partie."
                    : `${leconsVues} explication(s) déjà lue(s) dans cette partie.`}
                </p>
              </DessousBloc>
              <DessousBloc titre="Tableau de bord complet">
                <p className="dessous-texte">
                  Toutes les jauges et le panneau des rivaux, visibles dès le premier tour — comme
                  avant. Sans ce réglage, chaque jauge ({jaugesDuHud}) apparaît la première
                  fois qu'elle bouge, et reste ensuite affichée.
                </p>
              </DessousBloc>
              <DessousBloc titre="Tolérance de timing">
                <p className="dessous-texte">
                  Fenêtres et chronos allongés de 30 %. En échange, les scores de mini-jeu sont
                  plafonnés à 85 — pour que ce soit une aide, pas un raccourci. À changer entre
                  deux étapes, jamais au milieu d'une.
                  {compte
                    ? " Côté compte, le réglage part au serveur : il s'applique au prochain pattern généré."
                    : ""}
                </p>
              </DessousBloc>
              <DessousBloc titre="Le manager tranche les événements après un délai">
                <p className="dessous-texte">
                  Avec un manager en poste, la pop-up d'un événement porte un compte à rebours réel.
                  Expiré sans réponse, il tranche à ta place — selon son style. Toute interaction
                  avec la pop-up annule le compte à rebours : tu gardes la main, toujours. Sans
                  manager, ou réglage éteint, rien ne change : l'événement bloque comme aujourd'hui.
                  {compte ? " Côté compte, le réglage part au serveur." : ""}
                </p>
              </DessousBloc>
            </LesDessous>
          </span>
        </legend>

        {/* ⟦A-9⟧ « Une fois par run, réactivable dans les Réglages. » Le réglage est en tête :
            c'est celui qu'un joueur perdu vient chercher, et il ne doit pas être à scroller.
            Le bouton de remise à zéro vit HORS du `<label>` — dedans, cliquer dessus basculerait
            la case en même temps. */}
        <div className="reglage reglage-decouverte">
          <label className="reglage-corps">
            <input
              type="checkbox"
              checked={settings.decouverte}
              onChange={(e) => setSetting("decouverte", e.target.checked)}
            />
            <span>
              <strong>Mode découverte</strong>
            </span>
          </label>
          <div className="reglage-actions">
            <button
              type="button"
              className="bouton discret"
              disabled={leconsVues === 0}
              title="Tout réexpliquer depuis le début"
              onClick={reinitialiserDecouverte}
            >
              ↺ Tout réexpliquer
            </button>
            <span className="reglage-aide">{lues}</span>
          </div>
        </div>

        {/* ⟦A-129 · C11⟧ Le HUD progressif : à la création d'une partie, seules abonnés + énergie
            + santé mentale sont visibles, et chaque autre jauge apparaît quand elle bouge pour la
            première fois. Ce réglage court-circuite tout, pour qui préfère tout voir d'emblée. */}
        <label className="reglage">
          <input
            type="checkbox"
            checked={settings.hudComplet}
            onChange={(e) => setSetting("hudComplet", e.target.checked)}
          />
          <span>
            <strong>Tableau de bord complet</strong>
          </span>
        </label>

        <label className="reglage">
          <input
            type="checkbox"
            checked={settings.tolerance}
            onChange={(e) => {
              setSetting("tolerance", e.target.checked);
              // ⟦§5.9⟧ Côté compte, la tolérance vit dans l'ÉTAT (`flags.accessibility_tolerance`)
              // et pilote la génération du pattern comme son recalcul : un interrupteur qui ne le
              // dirait pas au serveur serait un interrupteur qui ne fait rien.
              void synchroniserTolerance(e.target.checked);
            }}
          />
          <span>
            <strong>Tolérance de timing</strong>
            <span className="reglage-aide">+30 % de temps, score plafonné à 85</span>
          </span>
        </label>

        {/* ⟦C8 · A-120⟧ Opt-in, défaut OFF — sans manager en poste OU réglage éteint, un
            événement bloque exactement comme avant (⟦A-118 §1⟧, l'état neutre du chantier). */}
        <label className="reglage">
          <input
            type="checkbox"
            checked={settings.managerDelaiActif}
            onChange={(e) => {
              setSetting("managerDelaiActif", e.target.checked);
              void synchroniserManagerDelai(e.target.checked);
            }}
          />
          <span>
            <strong>Le manager tranche</strong>
          </span>
        </label>
      </fieldset>

      {/* ⟦A-8⟧ « Le mode ne touche JAMAIS le moteur (…) et l'UI doit l'énoncer, sans quoi
          "arcade" se lira comme un mode facile. » D'où la phrase en gras, non négociable : elle
          RESTE à l'écran (⟦C57⟧), le reste de l'aide passe dans les dessous. */}
      <fieldset className="reglage-bloc">
        <legend>
          <span className="reglage-legende">
            <strong>Quand tu publies</strong>
            <LesDessous titre="Quand tu publies" variante="icone" className="reglage-dessous">
              <DessousBloc titre="Ce que tu vois quand tu publies">
                <p className="dessous-texte">
                  <strong>Le suspense.</strong> Tu vois ce que TU as fait — la classe, ton score. Ce
                  que le public en fait arrive jour après jour.
                </p>
                <p className="dessous-texte">
                  <strong>Tout de suite.</strong> La courbe complète dès la publication : les vues
                  attendues et les jours où elles tomberont.
                </p>
                <p className="dessous-texte">
                  <strong>Ça ne change rien au jeu.</strong> Strictement rien : mêmes vues, mêmes
                  abonnés, même argent. C'est le moment où tu l'apprends qui change — et tu peux
                  basculer quand tu veux, y compris sur des vidéos déjà publiées.
                </p>
              </DessousBloc>
            </LesDessous>
          </span>
        </legend>
        <div className="choix-modes">
          <label className={`mode ${settings.revealMode === "reel" ? "actif" : ""}`}>
            <input
              type="radio"
              name="revealMode"
              checked={settings.revealMode === "reel"}
              onChange={() => {
                setSetting("revealMode", "reel");
                // ⟦A-8 / M2.40 · A-81⟧ Une préférence du joueur suit le joueur : elle part au compte.
                void synchroniserRevelation("reel");
              }}
            />
            <strong>Le suspense</strong>
            <span>ton score d'abord</span>
          </label>
          <label className={`mode ${settings.revealMode === "arcade" ? "actif" : ""}`}>
            <input
              type="radio"
              name="revealMode"
              checked={settings.revealMode === "arcade"}
              onChange={() => {
                setSetting("revealMode", "arcade");
                void synchroniserRevelation("arcade");
              }}
            />
            <strong>Tout de suite</strong>
            <span>la courbe complète</span>
          </label>
        </div>
        <span className="reglage-aide">
          <strong>Ça ne change rien au jeu.</strong>
        </span>
      </fieldset>

      <fieldset className="reglage-bloc">
        <legend>
          <span className="reglage-legende">
            <strong>Son et calage</strong>
            <LesDessous titre="Son et calage" variante="icone" className="reglage-dessous">
              <DessousBloc titre="Le son">
                <p className="dessous-texte">
                  18 effets, piochés dans la banque locale — du papier qu'on tourne, du carton qu'on
                  froisse, du scotch qu'on déchire. Aucun n'est généré.
                </p>
              </DessousBloc>
              <DessousBloc titre="Calage audio">
                <p className="dessous-texte">
                  Si ton casque ou ta carte son retardent, tu frappes en retard sans le savoir. Le
                  Montage corrige chaque frappe de cette valeur avant de la noter — que la musique
                  joue ou non. Positif : tu frappes après le temps ; négatif : avant.
                </p>
                <p className="dessous-texte">
                  Le test : {BEATS_TEST} temps à {BPM_TEST} BPM, tape Espace sur chaque temps.
                </p>
              </DessousBloc>
              <DessousBloc titre="Mouvement réduit">
                <p className="dessous-texte">
                  {reducedMotion
                    ? "Détecté — les mini-jeux passent en mode posé, sans défilement."
                    : "Non détecté. Il se lit dans ton système, il ne se règle pas ici."}
                </p>
              </DessousBloc>
            </LesDessous>
          </span>
        </legend>

        <label className="reglage">
          <input
            type="checkbox"
            checked={!settings.mute}
            onChange={(e) => setSetting("mute", !e.target.checked)}
          />
          <span>
            <strong>Le son</strong>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={settings.volume}
              disabled={settings.mute}
              aria-label="Volume"
              onChange={(e) => setSetting("volume", Number(e.target.value))}
            />
            <span className="reglage-aide">volume {Math.round(settings.volume * 100)} %</span>
          </span>
        </label>

        <div className="reglage reglage-calage">
          <strong>Calage audio</strong>
          <input
            type="range"
            min={OFFSET_MIN_MS}
            max={OFFSET_MAX_MS}
            step={OFFSET_PAS_MS}
            value={settings.audioOffsetMs}
            aria-label="Calage audio en millisecondes"
            onChange={(e) => setSetting("audioOffsetMs", Number(e.target.value))}
          />
          <span className="reglage-aide">
            {settings.audioOffsetMs > 0 ? "+" : ""}
            {settings.audioOffsetMs} ms
            {settings.audioOffsetMs !== 0 ? (
              <>
                {" "}
                ·{" "}
                <button className="lien" type="button" onClick={() => setSetting("audioOffsetMs", 0)}>
                  remettre à zéro
                </button>
              </>
            ) : null}
          </span>
          {/* §7.7 — le mini-test : 8 temps à 90 BPM, Espace, la médiane proposée. */}
          <CalageAudio />
        </div>

        <p className="ligne-info discret">
          Mouvement réduit : {reducedMotion ? "détecté" : "non détecté"}
        </p>
      </fieldset>

      {/* Où la partie est calculée — la seule information de cet écran qui change le sens des
          autres écrans (un game over en mode normal ne veut pas dire la même chose des deux
          côtés). ⟦C57⟧ Dite en deux ou trois mots à l'écran, en toutes lettres dans les dessous. */}
      <fieldset className="reglage-bloc">
        <legend>
          <span className="reglage-legende">
            <strong>Ta partie</strong>
            <LesDessous titre="Ta partie" variante="icone" className="reglage-dessous">
              <DessousBloc titre="Où ta partie est calculée">
                {compte ? (
                  <p className="dessous-texte">
                    <strong>Sur le serveur</strong>, en base, à chaque action — connecté
                    {utilisateur ? ` en tant que ${utilisateur.name} (${utilisateur.email})` : ""}.
                    Le hardcore engage vraiment, la victoire se grave, et une chute en mode normal
                    te ramène au dernier palier au lieu de finir la run.
                  </p>
                ) : (
                  <p className="dessous-texte">
                    <strong>Dans ce navigateur</strong> — aucun compte, aucun serveur. Tout le jeu
                    s'y joue, production comprise, et la graine s'y choisit. En échange : rien n'y
                    est vérifié, rien n'y est sauvegardé ailleurs, et aucune victoire n'y est
                    gravée.
                  </p>
                )}
              </DessousBloc>
              <DessousBloc titre="Changer d'endroit">
                <p className="dessous-texte">
                  {compte
                    ? "Tu retournes au menu. Ta run de compte reste intacte sur le serveur : tu la retrouveras en revenant, au tour exact où tu l'as laissée."
                    : "Tu retournes au menu. Ta partie de bac à sable reste dans ce navigateur : elle t'attendra ici, telle quelle."}
                </p>
                {compte ? (
                  <p className="dessous-texte">
                    Se déconnecter : ta chaîne reste en base, au tour exact. Il suffira de te
                    reconnecter pour la reprendre.
                  </p>
                ) : null}
              </DessousBloc>
              <DessousBloc titre="Historique des parties">
                <p className="dessous-texte">
                  Chaque chaîne finie garde sa fiche : abonnés, jours simulés, raison de la fin.
                </p>
              </DessousBloc>
              {/* ⟦A-141 · C17⟧ `!onClose` : voir plus bas, « Menu principal » n'existe pas quand
                  ces Réglages sont ouverts depuis le menu. */}
              {run && !onClose ? (
                <DessousBloc titre="Menu principal">
                  <p className="dessous-texte">
                    « {run.channelName} » continue d'exister exactement comme tu l'as laissée — ce
                    bouton ne ferme rien, il ramène juste à l'écran-titre. « Charger » t'y ramènera
                    d'un clic.
                  </p>
                </DessousBloc>
              ) : null}
              {run && run.seed ? (
                <DessousBloc titre="Graine de cette run">
                  <p className="dessous-texte">
                    Graine de cette run : <code>{run.seed}</code> — la rejouer à l'identique, c'est
                    la recopier à la création d'une nouvelle chaîne.
                  </p>
                </DessousBloc>
              ) : null}
              {run && !run.seed ? (
                <DessousBloc titre="Graine de cette run">
                  <p className="dessous-texte">
                    La graine de cette run reste au serveur : c'est elle qui rend les événements et
                    les patterns d'Atelier imprévisibles. On ne peut donc pas la rejouer.
                  </p>
                </DessousBloc>
              ) : null}
            </LesDessous>
          </span>
        </legend>
        {compte ? (
          <>
            <span className="reglage-aide reglage-etat">
              Calculée <strong>sur le serveur</strong>
              {utilisateur ? ` · ${utilisateur.name}` : ""}
            </span>
            <div className="reglage-actions">
              <button type="button" className="bouton discret" onClick={() => void seDeconnecter()}>
                ⎋ Me déconnecter
              </button>
              <button type="button" className="bouton discret" onClick={() => void changerDeMonde()}>
                ↔ Jouer en bac à sable
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="reglage-aide reglage-etat">
              Calculée <strong>dans ce navigateur</strong>
            </span>
            <div className="reglage-actions">
              <button type="button" className="bouton discret" onClick={() => void changerDeMonde()}>
                ↔ Passer par un compte
              </button>
            </div>
          </>
        )}

        {/* ⟦C23 §2⟧ Retour de Matt sur la fenêtre Actions de la chambre : « ça n'a rien à faire
            dans le menu du bas, passe-les dans les paramètres. » L'historique existe qu'une run
            soit en cours ou non (même porte que `NouvelleRun.tsx`, étape ①) : jamais gêné par
            `onClose` ni par `run`. */}
        <div className="reglage-actions">
          <button type="button" className="bouton discret" onClick={() => naviguer("/historique")}>
            📜 Historique des parties
          </button>
          {/* ⟦A-141 · C17⟧ Revenir à l'écran-titre SANS rien abandonner : la run reste en mémoire,
              « Charger » y ramène instantanément. C'est le pendant non destructif du groupe
              « Abandonner », juste en dessous, exprès, pour qu'on lise les deux options avant de
              choisir laquelle on veut.
              `!onClose` : quand ces Réglages sont déjà ouverts DEPUIS le menu (`onClose` fourni,
              `Menu.tsx`), le bouton « ✕ Fermer » du panneau fait déjà exactement ça — le redire ici
              serait un deuxième bouton pour la même action, sans effet visible de plus (le menu ne
              bouge pas tant que ce panneau reste affiché). */}
          {run && !onClose ? (
            <button type="button" className="bouton discret" onClick={allerAuMenu}>
              ⏻ Menu principal
            </button>
          ) : null}
        </div>

        {run && run.seed ? (
          <p className="ligne-info discret">
            Graine : <code>{run.seed}</code>
          </p>
        ) : null}
        {run && !run.seed ? <p className="ligne-info discret">Graine : au serveur</p> : null}
      </fieldset>

      {/* ⟦A-97 §1.2⟧ La porte de sortie. En bas, dans son propre groupe, et jamais à côté d'un
          réglage qu'on bascule d'un clic — c'est la seule commande de cet écran qui termine
          une partie. */}
      {run ? (
        <fieldset className="reglage-bloc">
          <legend>
            <span className="reglage-legende">
              <strong>Abandonner cette chaîne</strong>
              <LesDessous
                titre="Abandonner cette chaîne"
                variante="icone"
                className="reglage-dessous"
              >
                <DessousBloc>
                  <p className="dessous-texte">
                    {compte ? (
                      <>
                        « {run.channelName} » se refermerait ici et rejoindrait ton{" "}
                        <strong>mémorial</strong> : une chaîne finie ne s'efface pas, elle se relit —
                        tu la retrouveras dans ton historique avec sa raison de fin. Mais on ne
                        revient pas dessus, et tu repartirais de zéro à côté.
                      </>
                    ) : (
                      <>
                        « {run.channelName} » se refermerait ici. En bac à sable il n'y a pas de
                        serveur : la partie est <strong>effacée de ce navigateur</strong>. Seule sa
                        fiche resterait dans ton historique — le nom, la fin, les abonnés. On ne
                        revient pas dessus.
                      </>
                    )}
                  </p>
                </DessousBloc>
              </LesDessous>
            </span>
          </legend>
          <div className="reglage-actions">
            <button
              type="button"
              className="bouton danger"
              onClick={() => void abandonnerLaChaine()}
            >
              🚪 Abandonner cette chaîne
            </button>
          </div>
        </fieldset>
      ) : null}
    </Panneau>
  );
}
